import Config from './Config.ts';
import {
  reportRtaClientFailure,
  type RtaFailureReporter,
} from './rta-observability.ts';

const PUBLIC_REQUESTABLE_EVENTS_BY_PRODUCTION_QUERY = `
  query PublicRequestableEventsByProduction($productionId: ID!) {
    publicRequestableEvents(productionId: $productionId) {
      event_id
      event_inventory_id
      event_name
      date
      time
      inventory_name
    }
  }
`;

/** One server-authorized event inventory that may accept a public attendance request. */
export interface PublicRequestableEvent {
  event_id: string;
  event_inventory_id: string;
  event_name: string;
  date?: string | null;
  time?: string | null;
  inventory_name?: string | null;
}

/** Injectable transport options for the production-scoped requestable-events query. */
export interface RtaFetchOptions {
  endpoint?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  reportFailure?: RtaFailureReporter;
}

/** Resolve the public GraphQL endpoint from booking-web's configured API origin. */
export const getPublicGraphqlEndpoint = (): string =>
  new URL('/graphql/public', Config.data.uri.api).toString();

const isNullableString = (value: unknown): value is string | null | undefined =>
  value === undefined || value === null || typeof value === 'string';

const isPublicRequestableEvent = (value: unknown): value is PublicRequestableEvent => {
  if (typeof value !== 'object' || value === null) return false;

  const row = value as Record<string, unknown>;
  return (
    typeof row.event_id === 'string' &&
    row.event_id.length > 0 &&
    typeof row.event_inventory_id === 'string' &&
    row.event_inventory_id.length > 0 &&
    typeof row.event_name === 'string' &&
    row.event_name.length > 0 &&
    isNullableString(row.date) &&
    isNullableString(row.time) &&
    isNullableString(row.inventory_name)
  );
};

/**
 * Fetch server-filtered requestable inventories for one production.
 *
 * Every transport, HTTP, JSON, GraphQL, or response-shape failure returns an
 * empty array so the widget fails closed and keeps the normal booking action.
 */
export async function fetchPublicRequestableEventsByProduction(
  productionId: string,
  options: RtaFetchOptions = {}
): Promise<PublicRequestableEvent[]> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const reportFailure = options.reportFailure ?? reportRtaClientFailure;

  try {
    const response = await fetchImpl(options.endpoint ?? getPublicGraphqlEndpoint(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      signal: options.signal,
      body: JSON.stringify({
        query: PUBLIC_REQUESTABLE_EVENTS_BY_PRODUCTION_QUERY,
        variables: { productionId },
      }),
    });

    if (!response.ok) {
      reportFailure({ operation: 'eligibility', kind: 'http', status: response.status });
      return [];
    }

    const payload = (await response.json()) as {
      data?: { publicRequestableEvents?: unknown };
      errors?: unknown;
    };

    if (
      payload.errors !== undefined &&
      (!Array.isArray(payload.errors) || payload.errors.length > 0)
    ) {
      reportFailure({ operation: 'eligibility', kind: 'graphql' });
      return [];
    }

    const rows = payload.data?.publicRequestableEvents;
    if (!Array.isArray(rows) || !rows.every(isPublicRequestableEvent)) {
      reportFailure({ operation: 'eligibility', kind: 'malformed' });
      return [];
    }

    return rows;
  } catch {
    if (!options.signal?.aborted) {
      reportFailure({ operation: 'eligibility', kind: 'transport' });
    }
    return [];
  }
}
